import { isoCountryMap } from "./countryData"
import type { PctLookupApplicant } from "../services/epoOpsService"

// The EPO data service only knows the name of an applicant (and sometimes the
// country), never the address. These records are therefore incomplete on purpose:
// the person filling in the form opens each one and adds the address.

// "EXAMPLE, INC." is a company, "MUSTER, Maria" is a person
const COMPANY_ENDING = /\b(inc|incorporated|ltd|limited|llc|lp|corp|corporation|co|gmbh|kg|ag|sa|sas|nv|bv|ab|oy|as|plc|kk|spa|srl)\.?$/i

const startCase = (value: string) =>
  value === value.toUpperCase()
    ? value.toLowerCase().replace(/(^|[\s'-])(\p{L})/gu, (_, before: string, letter: string) => before + letter.toUpperCase())
    : value

export function toApplicantRecord(applicant: PctLookupApplicant) {
  const country = (applicant.countryCode && isoCountryMap[applicant.countryCode]) || ""
  const comma = applicant.name.indexOf(",")
  const isPerson = comma > 0 && !COMPANY_ENDING.test(applicant.name.slice(comma + 1).trim())

  const common = {
    commonRepresentative: false,
    isAlsoInventor: false,
    countryOfResidence: country || undefined,
    address: { city: "", country },
    contact: {}
  }

  return isPerson
    ? {
        ...common,
        type: "natural" as const,
        lastName: startCase(applicant.name.slice(0, comma).trim()),
        firstName: applicant.name.slice(comma + 1).trim()
      }
    : { ...common, type: "legal" as const, legalName: applicant.name.trim() }
}

const nameOf = (applicant: any) =>
  (applicant.type === "legal" ? applicant.legalName : `${applicant.lastName ?? ""} ${applicant.firstName ?? ""}`)
    .trim()
    .toLowerCase()

// Applicants the form already has are not added a second time
export function addRetrievedApplicants(current: any[] | null, retrieved: PctLookupApplicant[]) {
  const existing = current ?? []
  const known = new Set(existing.filter((applicant) => !applicant.isDeleted).map(nameOf))
  const added = retrieved.map(toApplicantRecord).filter((applicant) => !known.has(nameOf(applicant)))

  return { list: [...existing, ...added], addedCount: added.length }
}

// What the EPO needs from every applicant: a name, a country and a city
export function isApplicantComplete(applicant: any) {
  const hasName = applicant.type === "legal" ? Boolean(applicant.legalName) : Boolean(applicant.lastName && applicant.firstName)

  return hasName && Boolean(applicant.countryOfResidence) && Boolean(applicant.address?.city) && Boolean(applicant.address?.country)
}
